import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, onMounted, inject } from 'vue';
import { useLogger } from 'vue-logger-plugin';
import { DeviceService } from '../../services/device.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'Affix',
  props: {
    offset: {
        type: Number,
        default: 55
    },
    containerClass: {
        type: String,
        default: ''
    },
    includeScrollFix: {
        type: Boolean,
        default: false
    },
    scrollHeight: {
        type: Number,
        default: 0
    },
    allowOnMobile: {
        type: Boolean,
        default: false
    }
},
  setup(__props) {

const props = __props;

// Use absloute path
const deviceService: DeviceService | undefined = inject('DeviceService');

// Incoming props


// Initialize the affix functionality and other variables
const logger = useLogger();
const affixElem = ref<HTMLDivElement>();

const affixState = ref({
    affixTop: true,
    affix: false,
    affixBottom: false
});

const affixStyles = ref({});
const containerStyles = ref({});

const config = {
    scrollThrottle: 100,
    navigationOffset: 0
};

const offset = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: Math.round(rect.top + scrollTop), left: Math.round(rect.left + scrollLeft) };
};

// only initialize affix if this is not a phone or phones are explicitly allowed
if (props.allowOnMobile || !deviceService?.isPhone()) {
    onMounted(() => {
        // verify we have access to the affixed element
        if (affixElem.value == null) {
            logger.error('Unable to get reference to affixed element.');
            return;
        }

        // after affix mounts, we need to fix any nested ads (if there are any)
        if (window?.rad?.ads?.updateObserver) {
            window.rad.ads.updateObserver(affixElem.value);
        }

        // Find containing row element if a fixed scroll height isn't provided
        let row = props.scrollHeight == 0 ? affixElem.value.parentElement : null;
        if (props.scrollHeight == 0) {
            const className = props.containerClass || 'row';
            while (row && row.parentElement && !row.classList.contains(className)) {
                row = row.parentElement;
            }
            

            // Only affix the element if the containing row is found
            if (row?.offsetHeight == 0 || !row?.classList.contains(className)) {
                logger.error('ERROR: Failed to affix element. Could not find parent row element.');
                return;
            }

            // Only affix the element if the containing row is at least 1.5 times its own height
            if (row?.offsetHeight < 1.5 * affixElem.value.offsetHeight) {
                logger.warn('WARNING: Element not affixed because parent row is too small.');
                return;
            }
        }

        // Calculate height of affix element
        let affixElemHeight = affixElem.value.offsetHeight;
        // If this fails, add up height of children
        if (affixElemHeight == 0) {
            for (let i = 0; i < affixElem.value.children.length; i++) {
                const child = affixElem.value.children[i] as HTMLElement;
                affixElemHeight += child.offsetHeight;
            }
        }

        // set a fixed height on the container to ensure it doesn't collapse when we start scrolling
        containerStyles.value = {
            height: `${affixElemHeight}px`
        };

        // Calculate offsets
        let skinOffset = document.body.classList.contains('enable-skin') ? 120 : 0;
        const topOffset = (offset(affixElem.value)?.top || 0) - skinOffset - config.navigationOffset - props.offset;
        const scrollHeight = props.scrollHeight > 0 ? props.scrollHeight : row?.offsetHeight || 0;
        let bottomOffset = topOffset + scrollHeight - affixElemHeight;
        const leftOffset = offset(affixElem.value)?.left || 0;

        logger.debug(`Affix Metrics: { topOffset: ${topOffset}, scrollHeight: ${scrollHeight}, bottomOffset: ${bottomOffset}, leftOffset: ${leftOffset}, affixElemHeight: ${affixElemHeight} }`);

        // Original width to force it to stick
        const origWidth = affixElem.value.offsetWidth;
        affixStyles.value = {
            width: origWidth + 'px'
        };

        let lastScrollTop = 0;

        logger.debug('setting scroll listener on window');

        const handleScroll = () => {
            const st = window.scrollY;
            skinOffset = document.body.classList.contains('enable-skin') && st > lastScrollTop ? 120 : 0;

            if (affixElem.value == null || scrollHeight == 0) {               
                // affix back to top
                affixState.value = {
                    affixTop: true,
                    affix: false,
                    affixBottom: false
                };
                return;
            }

            // UPDATE AFFIX ELEM HEIGHT
            // Calculate height of affix element
            affixElemHeight = affixElem.value.offsetHeight || 0;
            // If this fails, add up height of children
            if (affixElemHeight == 0) {
                for (let i = 0; i < affixElem.value.children.length; i++) {
                    const child = affixElem.value.children[i] as HTMLElement;
                    affixElemHeight += child.offsetHeight;
                }
            }
            if (affixElemHeight === 0) {
                return;
            }

            // update container height based on affixed element height
            containerStyles.value = {
                height: `${affixElemHeight}px`
            };

            // recalculate bottom offset
            bottomOffset = topOffset + scrollHeight - affixElemHeight;

            if (window.scrollY < topOffset) {
                // we're above the affixed element
                affixState.value = {
                    affixTop: true,
                    affix: false,
                    affixBottom: false
                };

                affixStyles.value = {
                    width: `${origWidth}px`
                };
            } else if (window.scrollY > bottomOffset) {
                // we're scrolling past the affixed element
                affixState.value = {
                    affixTop: false,
                    affix: false,
                    affixBottom: true
                };

                affixStyles.value = {
                    transform: `translateY(${scrollHeight - affixElemHeight}px)`,
                    width: `${origWidth}px`
                };
            } else {
                // we're currently affixed and scrolling
                affixState.value = {
                    affixTop: false,
                    affix: true,
                    affixBottom: false
                };

                affixStyles.value = {
                    width: `${origWidth}px`,
                    left: `${leftOffset}px`,
                    top: `${props.offset}px`
                };
            }

            lastScrollTop = st;
        };

        window.addEventListener('scroll', handleScroll);
    });
} else {
    logger.debug('Device is mobile, will not initialize Affix');
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "affix-container",
    style: _normalizeStyle(containerStyles.value)
  }, [
    _createElementVNode("div", {
      ref_key: "affixElem",
      ref: affixElem,
      class: _normalizeClass(["affix-elem", affixState.value]),
      style: _normalizeStyle(affixStyles.value)
    }, [
      _renderSlot(_ctx.$slots, "default")
    ], 6)
  ], 4))
}
}

})